import React, { useMemo, useEffect, useState } from 'react';
import { DatePickerInput } from '@mantine/dates';
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { Text, Badge, Paper, Stack, SimpleGrid } from '@mantine/core';
import dayjs from 'dayjs';
import { GET_OFFLINE_STORES } from '../../apollo/queries/offlineStores';
import { showErrorNotification } from '../../notifications';

interface OfflineStore {
    id: string;
    storeNo: string;
    name?: string | null;
    division?: string | null;
    opsArea?: string | null;
    countryCode?: string | null;
    posSystem?: string | null;
}

export function OfflineStoresReport() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
        const dateParam = searchParams.get('date');
        if (dateParam) {
            const parsed = dayjs(dateParam).toDate();
            return Number.isNaN(parsed.getTime())
                ? dayjs().subtract(1, 'day').toDate()
                : parsed;
        }
        return dayjs().subtract(1, 'day').toDate();
    });

    useEffect(() => {
        const dateParam = searchParams.get('date');
        if (dateParam) {
            const parsed = dayjs(dateParam).toDate();
            if (!Number.isNaN(parsed.getTime())) {
                setSelectedDate(parsed);
            } else {
                setSelectedDate(dayjs().subtract(1, 'day').toDate());
            }
        } else {
            setSelectedDate(dayjs().subtract(1, 'day').toDate());
        }
    }, [searchParams]);

    const { data, loading, error } = useQuery(GET_OFFLINE_STORES, {
        variables: {
            date: selectedDate
                ? `${dayjs(selectedDate).format('YYYY-MM-DD')}T00:00:00.000Z`
                : `${dayjs()
                      .subtract(1, 'day')
                      .format('YYYY-MM-DD')}T00:00:00.000Z`,
        },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (error) {
            showErrorNotification(
                `Failed to load offline stores: ${error.message}`,
            );
        }
    }, [error]);

    const offlineStores: OfflineStore[] = useMemo(
        () => data?.getOfflineStores || [],
        [data],
    );

    const linxasStores = useMemo(
        () => offlineStores.filter(store => store.posSystem === 'Linx'),
        [offlineStores],
    );

    const activeRetailStores = useMemo(
        () => offlineStores.filter(store => store.posSystem === 'ActiveRetail'),
        [offlineStores],
    );

    const storesWithoutPosSystem = useMemo(
        () => offlineStores.filter(store => !store.posSystem),
        [offlineStores],
    );

    const handleDateChange = (date: Date | null) => {
        setSelectedDate(date);
        if (date) {
            setSearchParams({ date: dayjs(date).format('YYYY-MM-DD') });
        } else {
            setSearchParams({});
        }
    };

    const columns = useMemo(
        (): DataTableColumn<OfflineStore>[] => [
            {
                accessor: 'storeNo',
                title: 'Store Number',
                width: 150,
                textAlign: 'left',
                sortable: true,
            },
            {
                accessor: 'name',
                title: 'Store Name',
                width: 250,
                textAlign: 'left',
                sortable: true,
                render: ({ name }) => name || 'N/A',
            },
            {
                accessor: 'division',
                title: 'Division',
                width: 200,
                textAlign: 'left',
                sortable: true,
                render: ({ division }) => division || 'N/A',
            },
            {
                accessor: 'opsArea',
                title: 'Ops Area',
                width: 200,
                textAlign: 'left',
                sortable: true,
                render: ({ opsArea }) => opsArea || 'N/A',
            },
            {
                accessor: 'countryCode',
                title: 'Country',
                width: 120,
                textAlign: 'left',
                sortable: true,
                render: ({ countryCode }) => countryCode || 'N/A',
            },
            {
                accessor: 'posSystem',
                title: 'POS System',
                width: 150,
                textAlign: 'left',
                sortable: true,
                render: ({ posSystem }) => {
                    if (!posSystem) {
                        return <Badge color="gray">N/A</Badge>;
                    }
                    return (
                        <Badge color={posSystem === 'Linx' ? 'blue' : 'green'}>
                            {posSystem === 'Linx' ? 'LinxAS' : 'ActiveRetail'}
                        </Badge>
                    );
                },
            },
        ],
        [],
    );

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <DatePickerInput
                        label="Select Date"
                        placeholder="Pick a date"
                        value={selectedDate}
                        onChange={value =>
                            handleDateChange(value as Date | null)
                        }
                        maxDate={new Date()}
                        clearable
                        style={{ width: 250 }}
                    />
                </div>
            </div>

            {selectedDate && (
                <>
                    <Paper p="lg" mb="md" withBorder>
                        <Stack gap="md">
                            <div>
                                <Text size="lg" fw={600} mb="xs">
                                    Summary Statistics
                                </Text>
                                <Text size="sm" c="dimmed">
                                    Date:{' '}
                                    {dayjs(selectedDate).format('YYYY-MM-DD')}
                                </Text>
                            </div>
                            <SimpleGrid
                                cols={{ base: 1, sm: 2, md: 4 }}
                                spacing="lg"
                            >
                                <Paper p="md" withBorder radius="lg">
                                    <Stack gap={4}>
                                        <Text size="sm" c="dimmed" fw={500}>
                                            Total Incomplete Sales
                                        </Text>
                                        <Text size="2xl" fw={700} c="red">
                                            {offlineStores.length}
                                        </Text>
                                    </Stack>
                                </Paper>
                                <Paper p="md" withBorder radius="lg">
                                    <Stack gap={4}>
                                        <Text size="sm" c="dimmed" fw={500}>
                                            LinxAS Incomplete
                                        </Text>
                                        <Text size="2xl" fw={700} c="blue">
                                            {linxasStores.length}
                                        </Text>
                                    </Stack>
                                </Paper>
                                <Paper p="md" withBorder radius="lg">
                                    <Stack gap={4}>
                                        <Text size="sm" c="dimmed" fw={500}>
                                            ActiveRetail Incomplete
                                        </Text>
                                        <Text size="2xl" fw={700} c="green">
                                            {activeRetailStores.length}
                                        </Text>
                                    </Stack>
                                </Paper>
                                {storesWithoutPosSystem.length > 0 && (
                                    <Paper p="md" withBorder radius="xl">
                                        <Stack gap={4}>
                                            <Text size="sm" c="dimmed" fw={500}>
                                                No POS System
                                            </Text>
                                            <Text size="2xl" fw={700} c="gray">
                                                {storesWithoutPosSystem.length}
                                            </Text>
                                        </Stack>
                                    </Paper>
                                )}
                            </SimpleGrid>
                        </Stack>
                    </Paper>

                    {offlineStores.length > 0 ? (
                        <DataTable
                            columns={columns}
                            records={offlineStores}
                            fetching={loading}
                            minHeight={200}
                            withTableBorder
                            striped
                            highlightOnHover
                            noRecordsText="No incomplete sales found for this date"
                        />
                    ) : (
                        <Paper p="md" withBorder>
                            <Text c="dimmed" ta="center">
                                {loading
                                    ? 'Loading...'
                                    : 'No incomplete sales found for this date'}
                            </Text>
                        </Paper>
                    )}
                </>
            )}
        </div>
    );
}
